
import { cn } from '@/lib/utils';
import { Badge, Button } from '@/components/ui';
import type { ConnectionState } from '@/hooks/useWebSocket';

function getStatusVariant(state: ConnectionState): 'success' | 'warning' | 'info' | 'danger' | 'default' {
  switch (state) {
    case 'connected': return 'success';
    case 'reconnecting': return 'warning';
    case 'connecting': return 'info';
    case 'disconnected': return 'danger';
    default: return 'default';
  }
}

function getStatusLabel(state: ConnectionState): string {
  switch (state) {
    case 'connected': return 'LIVE';
    case 'reconnecting': return 'Reconnecting...';
    case 'connecting': return 'Connecting...';
    case 'disconnected': return 'Offline';
    default: return 'Unknown';
  }
}

export interface LiveIndicatorProps {
  connectionState: ConnectionState;
  lastUpdate?: Date | null;
  newItemsCount: number;
  onClearNewItems?: () => void;
  /** Last server JSON pong time (ms); used with connectionState for health */
  lastPongAt?: number | null;
}

export function LiveIndicator({
  connectionState,
  newItemsCount,
  onClearNewItems,
  lastPongAt,
}: LiveIndicatorProps) {
  const isConnected = connectionState === 'connected';
  const isReconnecting = connectionState === 'reconnecting';
  const isConnecting = connectionState === 'connecting';

  const pongStale =
    isConnected &&
    typeof lastPongAt === 'number' &&
    Date.now() - lastPongAt > 45_000;

  return (
    <div className="flex items-center gap-4 flex-wrap">
      {/* Connection status */}
      <div className="flex items-center gap-2">
        {/* Status indicator */}
        <div className="relative overflow-hidden rounded-full">
          <div
            className={cn(
              'w-3 h-3 rounded-full',
              isConnected && 'bg-green-500',
              isReconnecting && 'bg-yellow-500',
              isConnecting && 'bg-blue-500',
              connectionState === 'disconnected' && 'bg-red-500'
            )}
          />
          {/* Pulse animation for connected state */}
          {isConnected && (
            <div className="absolute inset-0 w-3 h-3 rounded-full bg-green-500 animate-ping opacity-75" />
          )}
        </div>

        {/* Status badge */}
        <span
          title={
            typeof lastPongAt === 'number'
              ? `Last heartbeat: ${new Date(lastPongAt).toLocaleTimeString()}`
              : undefined
          }
        >
          <Badge
            variant={pongStale ? 'warning' : getStatusVariant(connectionState)}
            className="uppercase font-bold tracking-wider"
          >
            {pongStale ? 'Live (slow)' : getStatusLabel(connectionState)}
          </Badge>
        </span>
      </div>

      {/* New items notification */}
      {newItemsCount > 0 && onClearNewItems && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearNewItems}
          className="gap-1.5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 hover:bg-primary-200 dark:hover:bg-primary-900/50 animate-pulse"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <span>{newItemsCount} new item{newItemsCount > 1 ? 's' : ''}</span>
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </Button>
      )}
    </div>
  );
}
